import {Component, OnInit, ViewChild, ElementRef, AfterViewChecked, Inject, Optional, OnDestroy} from "@angular/core";
import {MatDialogRef, MAT_DIALOG_DATA} from '@angular/material/dialog';
import {Message} from "../../models/types";
import {AiService, ChatMessage} from "../../services/ai.service";
import {Router} from "@angular/router";
import {PromptEventService} from "../../services/prompt-event.service";
import {Subscription} from "rxjs";

@Component({
  selector: "app-chat-interface",
  templateUrl: "./chat-interface.component.html",
})
export class ChatInterfaceComponent implements OnInit, OnDestroy {
  @ViewChild("messagesEnd") messagesEnd!: ElementRef;

  isOpen = true;
  messages: Message[] = [];
  inputValue = "";
  charCount = 0;
  accentColor = "#FF6D03";
  disabledColor = "#FFA101";
  private promptSubscription: Subscription;

  suggestedPrompts = [
    "¿Qué galletas están disponibles en La Habana?",
    "Busca pollo en Matanzas",
    "Añade al carrito un paquete de caramelos de chocolate ricos besos montes"
  ];

  constructor(
    @Optional() public dialogRef: MatDialogRef<ChatInterfaceComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    private aiService: AiService,
    private router: Router,
    private promptEventService: PromptEventService
  ) {
    // Subscribe to prompt events
    this.promptSubscription = this.promptEventService.promptClicked$.subscribe(
      (promptText: string) => {
        this.handlePromptClick(promptText);
      }
    );
  }

  ngOnInit(): void {
    // Initialize messages from chat history if it exists
    if (this.aiService.chatHistory.length > 0) {
      this.initializeMessagesFromHistory();
    } else {
      // Use the default welcome message if no history
      this.messages = [
        {
          id: "1",
          content: "Bienvenido a Kompras, el asistente virtual de Katapulk. ¿En qué puedo ayudarte hoy?",
          sender: "assistant",
        }
      ];
    }
  }

  initializeMessagesFromHistory(): void {
    const history = this.aiService.chatHistory;
    this.messages = history.map((chatMsg: ChatMessage, index: number) => ({
      id: (index + 1).toString(),
      content: chatMsg.content,
      sender: chatMsg.role === 'user' ? 'user' : 'assistant',
    }));
  }

  clearHistory(): void {
    this.aiService.clearChatHistory();
    this.messages = [
      {
        id: "1",
        content: "Bienvenido a Kompras, el asistente virtual de Katapulk. ¿En qué puedo ayudarte hoy?",
        sender: "assistant",
      },
    ];
  }

  handleInputChange(event: any): void {
    this.inputValue = event.target.value;
    this.charCount = this.inputValue.length;
  }

  handleSubmit(event: Event): void {
    event.preventDefault();
    if (!this.inputValue.trim()) return;

    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      content: this.inputValue,
      sender: "user",
    };

    // Add bot typing indicator
    const botTypingMessage: Message = {
      id: (Date.now() + 1).toString(),
      content: "",
      sender: "assistant",
      isTyping: true,
    };

    this.messages = [...this.messages, userMessage, botTypingMessage];
    const userPrompt = this.inputValue;
    this.inputValue = "";
    this.charCount = 0;

    // Call the AI service to get a response
    this.aiService.sendChatMessage(userPrompt).subscribe({
      next: (response) => {
        const typingIndex = this.messages.findIndex((msg) => msg.isTyping);
        if (typingIndex !== -1) {
          const newMessages = [...this.messages];
          newMessages[typingIndex] = {
            id: newMessages[typingIndex].id,
            content: response.fails ? "Lo siento, hubo un error al procesar tu solicitud." : response.data,
            sender: "assistant",
          };
          this.messages = newMessages;
        }
      },
      error: (error) => {
        const typingIndex = this.messages.findIndex((msg) => msg.isTyping);
        if (typingIndex !== -1) {
          const newMessages = [...this.messages];
          newMessages[typingIndex] = {
            id: newMessages[typingIndex].id,
            content: "Lo siento, hubo un error al procesar tu solicitud. Por favor, intenta más tarde.",
            sender: "assistant",
          };
          this.messages = newMessages;
        }
        console.error('Error sending chat message:', error);
      }
    });
  }

  handlePromptClick(prompt: string): void {
    this.inputValue = prompt;
    this.charCount = prompt.length;
  }

  closeChat(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    } else {
      // If not in dialog mode, navigate back or to home
      this.router.navigate(['/']);
    }
  }

  ngOnDestroy(): void {
    // Clean up subscription to prevent memory leaks
    if (this.promptSubscription) {
      this.promptSubscription.unsubscribe();
    }
  }
}
